import { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import type { CSSProperties, DragEvent, Key, ReactNode } from 'react';
import {
  Card,
  CardBody,
  Button,
  Chip,
  Spinner,
  Divider,
  Input,
  Textarea,
  Modal,
  ModalBody,
  ModalContent,
  Autocomplete,
  AutocompleteItem,
  Tooltip,
  Select,
  SelectItem,
} from '@nextui-org/react';
import { listMarketComments } from 'common/src/api/market_comments';
import { getMarkets } from 'common/src/api/markets';
import {
  createMarketComment,
  uploadMarketCommentAttachment,
  deleteMarketCommentAttachment,
  getMarketCommentAttachmentSignedUrl,
  updateMarketComment,
  updateMarketCommentAttachment,
  deleteMarketComment,
  listMarketCommentTerms,
} from 'common/src/api/market_comments';
import { MarketComment, Market } from 'common/src/utils/types';
import { useTranslation } from 'common/src/hooks/useTranslation';
import useDataStore from 'common/src/stores/useDataStore';
import { useConfirm } from '../ui';

interface MarketCommentsSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  viewMode: 'day' | 'week';
  selectedDate?: string; // YYYY-MM-DD
  dateRange?: [Date, Date];
  speciesId?: number;
  season?: string;
  exporterId?: number;
  userId?: number;
  selectedMarket?: string; // markets.id (e.g., 'CNCAN'), optional
  onCountChange?: (count: number) => void;
  onBitacoraChange?: () => void; // Called when comments with prices are added/edited/deleted
  variant?: 'sidebar' | 'embedded';
  loadAll?: boolean; // Load all comments without date restrictions
  initialFilters?: Partial<{
    search: string;
    variety: string;
    caliber: string;
    seller: string;
    label: string;
    exporter_ref: string;
    container_number: string;
  }>;
}

// Local media query hook (mirrors filters sidebar behavior)
const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== 'undefined') return window.matchMedia(query).matches;
    return false;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia(query);
    setMatches(media.matches);
    const listener = (e: MediaQueryListEvent) => setMatches(e.matches);
    if (media.addEventListener) {
      media.addEventListener('change', listener);
      return () => media.removeEventListener('change', listener);
    } else {
      media.addListener(listener);
      return () => media.removeListener(listener);
    }
  }, [query]);
  return matches;
};

export default function MarketCommentsSidebar({
  isOpen,
  onToggle,
  viewMode,
  selectedDate,
  dateRange,
  speciesId,
  season,
  exporterId,
  userId,
  selectedMarket,
  onCountChange,
  onBitacoraChange,
  variant = 'sidebar',
  loadAll = false,
  initialFilters,
}: MarketCommentsSidebarProps) {
  const { t } = useTranslation('market');
  const confirm = useConfirm();
  const { selectedSpecies } = useDataStore();
  const modalPortalContainer =
    typeof document !== 'undefined' ? document.body : undefined;
  const speciesColors = selectedSpecies?.colors || [];
  const isMd = useMediaQuery('(min-width: 768px)');
  const [comments, setComments] = useState<MarketComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  type SelectedFile = {
    file: File;
    status: 'queued' | 'uploading' | 'done' | 'error';
    progress?: number; // reserved for future if SDK exposes progress
    previewUrl?: string; // object URL for images
    note?: string; // user-provided note to store with attachment
  };
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [imageUrlCache, setImageUrlCache] = useState<Record<number, string>>(
    {}
  );
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingContent, setEditingContent] = useState<string>('');
  const [editingAttachment, setEditingAttachment] = useState<{
    id: number;
    value: string;
  } | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [imageViewer, setImageViewer] = useState<{
    url: string;
    name: string;
    id: number;
    bucket: string;
    path: string;
    created_by: number;
    commentId: number;
    comment?: string;
    is_image?: boolean;
  } | null>(null);
  const [viewerEdit, setViewerEdit] = useState(false);
  const [viewerNote, setViewerNote] = useState('');
  const [terms, setTerms] = useState<{
    varieties: string[];
    calibers: string[];
    sellers: string[];
    labels: string[];
    exporter_refs: string[];
    container_numbers: string[];
  }>({
    varieties: [],
    calibers: [],
    sellers: [],
    labels: [],
    exporter_refs: [],
    container_numbers: [],
  });
  const [showLists, setShowLists] = useState({
    variety: false,
    caliber: false,
    seller: false,
    label: false,
    exporter_ref: false,
    container_number: false,
  });
  const [form, setForm] = useState({
    content: '',
    date_from: '',
    date_to: '',
    variety: '',
    caliber: '',
    color: '',
    ship_name: '',
    market_id: '',
    fleet: '' as '' | 'A' | 'M',
    seller: '',
    exporter_ref: '',
    container_number: '',
    label: '',
    unit_price: '' as string,
    unit_weight: '' as string,
    local_currency: '' as string,
    unit_count: '' as string,
  });
  const [markets, setMarkets] = useState<Market[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filterForm, setFilterForm] = useState(() => ({
    search: '',
    variety: '',
    caliber: '',
    seller: '',
    label: '',
    exporter_ref: '',
    container_number: '',
    ...initialFilters,
  }));

  const params = useMemo(() => {
    // Load all comments without date restrictions
    if (loadAll) {
      return {
        species_id: speciesId,
        season,
        market_id: selectedMarket,
        variety: filterForm.variety || undefined,
        caliber: filterForm.caliber || undefined,
        seller: filterForm.seller || undefined,
        label: filterForm.label || undefined,
        exporter_ref: filterForm.exporter_ref || undefined,
        container_number: filterForm.container_number || undefined,
        limit: 500,
      } as const;
    }
    if (viewMode === 'day' && selectedDate) {
      return {
        date: selectedDate,
        species_id: speciesId,
        season,
        market_id: selectedMarket,
        variety: filterForm.variety || undefined,
        caliber: filterForm.caliber || undefined,
        seller: filterForm.seller || undefined,
        label: filterForm.label || undefined,
        exporter_ref: filterForm.exporter_ref || undefined,
        container_number: filterForm.container_number || undefined,
        limit: 100,
      } as const;
    }
    if (viewMode !== 'day' && dateRange) {
      const [from, to] = dateRange;
      const fromStr = from.toISOString().split('T')[0];
      const toStr = to.toISOString().split('T')[0];
      return {
        from: fromStr,
        to: toStr,
        species_id: speciesId,
        season,
        market_id: selectedMarket,
        variety: filterForm.variety || undefined,
        caliber: filterForm.caliber || undefined,
        seller: filterForm.seller || undefined,
        label: filterForm.label || undefined,
        exporter_ref: filterForm.exporter_ref || undefined,
        container_number: filterForm.container_number || undefined,
        limit: 200,
      } as const;
    }
    if (filterForm.exporter_ref || filterForm.container_number) {
      return {
        species_id: speciesId,
        season,
        market_id: selectedMarket,
        variety: filterForm.variety || undefined,
        caliber: filterForm.caliber || undefined,
        seller: filterForm.seller || undefined,
        label: filterForm.label || undefined,
        exporter_ref: filterForm.exporter_ref || undefined,
        container_number: filterForm.container_number || undefined,
        limit: 200,
      } as const;
    }
    return null;
  }, [
    loadAll,
    viewMode,
    selectedDate,
    dateRange,
    speciesId,
    season,
    selectedMarket,
    filterForm.variety,
    filterForm.caliber,
    filterForm.seller,
    filterForm.label,
    filterForm.exporter_ref,
    filterForm.container_number,
  ]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!isOpen || !params) return;
      setLoading(true);
      setError(null);
      try {
        const data = await listMarketComments(params as any);
        const q = (filterForm.search || '').trim().toLowerCase();
        const filtered = !q
          ? data
          : data.filter((c) => {
              const hay = [
                c.content || '',
                c.variety || '',
                c.caliber || '',
                c.seller || '',
                c.label || '',
                c.exporter_ref || '',
                c.container_number || '',
              ]
                .join(' ')
                .toLowerCase();
              return hay.includes(q);
            });
        if (!cancelled) setComments(filtered);
        if (!cancelled && onCountChange) onCountChange((data || []).length);
      } catch (e: any) {
        if (!cancelled) setError('Error al cargar comentarios');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [isOpen, JSON.stringify(params), filterForm.search]);

  // Devuelve true si guardó, para que el modal solo se cierre en ese caso.
  const handleCreate = async (): Promise<boolean> => {
    if (!exporterId || !userId) return false;
    setError(null);
    // Read date range from editable form fields (preloaded from selection)
    const date_from = form.date_from?.trim();
    const raw_date_to = form.date_to?.trim();
    const date_to: string | null = raw_date_to ? raw_date_to : null;
    if (!date_from) return false;
    // La tabla exige date_to >= date_from (market_comments_valid_range). Al
    // duplicar un registro y cambiar solo "desde" quedaba al revés y el
    // guardado fallaba con un error genérico.
    if (date_to && date_to < date_from) {
      setError(t('La fecha "hasta" no puede ser anterior a la fecha "desde"'));
      return false;
    }

    const unitWeightNum = form.unit_weight
      ? Number(form.unit_weight)
      : undefined;
    const hasValidWeight =
      unitWeightNum !== undefined &&
      !Number.isNaN(unitWeightNum) &&
      unitWeightNum > 0;
    const unitPriceNum =
      hasValidWeight && form.unit_price ? Number(form.unit_price) : undefined;
    const unitCountNum = form.unit_count ? Number(form.unit_count) : undefined;
    setCreating(true);
    try {
      const payload = {
        exporter_id: exporterId,
        created_by: userId,
        content: form.content || (null as any),
        date_from,
        date_to,
        species_id: speciesId ?? null,
        season: season ?? null,
        market_id: form.market_id || (selectedMarket && selectedMarket !== 'all' ? selectedMarket : null),
        color: form.color || null,
        ship_name: form.ship_name || null,
        fleet: form.fleet || null,
        variety: form.variety || null,
        caliber: form.caliber || null,
        seller: form.seller || null,
        exporter_ref: form.exporter_ref || null,
        container_number: form.container_number || null,
        label: form.label || null,
        unit_price:
          unitPriceNum !== undefined && !Number.isNaN(unitPriceNum)
            ? unitPriceNum
            : null,
        unit_weight:
          unitWeightNum !== undefined && !Number.isNaN(unitWeightNum)
            ? unitWeightNum
            : null,
        local_currency: form.local_currency || null,
        unit_count:
          unitCountNum !== undefined && !Number.isNaN(unitCountNum)
            ? Math.round(unitCountNum)
            : null,
      };
      // Create or update depending on mode
      let created: any = null;
      if (isEditMode && editingCommentId) {
        const updated = await updateMarketComment(
          editingCommentId,
          payload as any
        );
        created = updated;
      } else {
        created = await createMarketComment(payload as any);
      }

      // Upload attachments after comment creation
      let attachments: any[] = [];
      if (selectedFiles.length > 0) {
        setUploadingAttachments(true);
        const results: any[] = [];
        for (let i = 0; i < selectedFiles.length; i++) {
          const sel = selectedFiles[i];
          // mark uploading
          setSelectedFiles((prev) => {
            const copy = [...prev];
            copy[i] = { ...copy[i], status: 'uploading', progress: undefined };
            return copy;
          });
          try {
            const row = await uploadMarketCommentAttachment({
              commentId: created.id,
              exporterId: exporterId,
              created_by: userId,
              file: sel.file,
              comment: sel.note?.trim() || undefined,
            });
            results.push(row);
            setSelectedFiles((prev) => {
              const copy = [...prev];
              copy[i] = { ...copy[i], status: 'done', progress: 100 };
              return copy;
            });
          } catch (e) {
            setSelectedFiles((prev) => {
              const copy = [...prev];
              copy[i] = { ...copy[i], status: 'error' };
              return copy;
            });
          }
        }
        attachments = results;
        setUploadingAttachments(false);
      }

      // Merge result into list (update existing or prepend new)
      const createdWithAttachments = { ...created, attachments } as any;
      setComments((prev) => {
        if (isEditMode && editingCommentId) {
          return prev.map((mc) =>
            mc.id === editingCommentId
              ? {
                  ...mc,
                  ...createdWithAttachments,
                  // Append newly uploaded attachments to existing ones
                  attachments: [
                    ...(mc.attachments || []),
                    ...(attachments || []),
                  ],
                }
              : mc
          );
        }
        return [createdWithAttachments, ...prev];
      });
      // Reset form
      setForm((prev) => ({
        // Keep dates preloaded from current selection
        content: '',
        date_from: prev.date_from,
        date_to: prev.date_to,
        variety: '',
        caliber: '',
        color: '',
        ship_name: '',
        market_id: '',
        fleet: '',
        seller: '',
        exporter_ref: '',
        container_number: '',
        label: '',
        unit_price: '',
        unit_weight: '',
        local_currency: prev.local_currency,
        unit_count: '',
      }));
      // Clear selected files after upload
      setSelectedFiles([]);
      setIsEditMode(false);
      setEditingCommentId(null);
      // Notify parent that bitácora data may have changed (for chart recalculation)
      if (onBitacoraChange) onBitacoraChange();
      return true;
    } catch (e) {
      const code = (e as { code?: string } | null)?.code;
      setError(
        code === '23514'
          ? t('La fecha "hasta" no puede ser anterior a la fecha "desde"')
          : t('Error al crear comentario')
      );
      return false;
    } finally {
      setCreating(false);
    }
  };

  // Open modal to edit an existing comment
  const openEditModal = (c: MarketComment) => {
    setIsEditMode(true);
    setEditingCommentId(c.id);
    setForm({
      content: c.content || '',
      date_from: c.date_from || '',
      date_to: c.date_to || '',
      variety: c.variety || '',
      caliber: c.caliber || '',
      color: c.color || '',
      ship_name: c.ship_name || '',
      market_id: c.market_id || '',
      fleet: c.fleet || '',
      seller: c.seller || '',
      exporter_ref: c.exporter_ref || '',
      container_number: c.container_number || '',
      label: c.label || '',
      unit_price: c.unit_price != null ? String(c.unit_price) : '',
      unit_weight: c.unit_weight != null ? String(c.unit_weight) : '',
      local_currency: c.local_currency || '',
      unit_count: c.unit_count != null ? String(c.unit_count) : '',
    });
    setSelectedFiles([]);
    setIsCreateModalOpen(true);
  };

  // Open modal to duplicate an existing comment (prefill but create new)
  const openDuplicateModal = async (c: MarketComment) => {
    setIsEditMode(false);
    setEditingCommentId(null);
    setForm({
      content: c.content || '',
      date_from: c.date_from || '',
      date_to: c.date_to || '',
      variety: c.variety || '',
      caliber: c.caliber || '',
      color: c.color || '',
      ship_name: c.ship_name || '',
      market_id: c.market_id || '',
      fleet: c.fleet || '',
      seller: c.seller || '',
      exporter_ref: c.exporter_ref || '',
      container_number: c.container_number || '',
      label: c.label || '',
      unit_price: c.unit_price != null ? String(c.unit_price) : '',
      unit_weight: c.unit_weight != null ? String(c.unit_weight) : '',
      local_currency: c.local_currency || '',
      unit_count: c.unit_count != null ? String(c.unit_count) : '',
    });
    setSelectedFiles([]);
    setIsCreateModalOpen(true);

    // Prefill attachments: download blobs via signed URLs and stage as new files
    try {
      const attachments = (c.attachments || []).filter((a) => !a.deleted_at);
      const staged: SelectedFile[] = [];
      for (const a of attachments) {
        try {
          const url = await getMarketCommentAttachmentSignedUrl(
            a.bucket,
            a.path,
            600
          );
          const resp = await fetch(url);
          if (!resp.ok) continue;
          const blob = await resp.blob();
          const fileName = a.name || 'attachment';
          const type =
            a.content_type || blob.type || 'application/octet-stream';
          const file = new File([blob], fileName, { type });
          const previewUrl = a.is_image ? URL.createObjectURL(blob) : undefined;
          staged.push({
            file,
            status: 'queued',
            previewUrl,
            note: a.comment || '',
          });
        } catch {}
      }
      if (staged.length > 0) setSelectedFiles(staged);
    } catch {}
  };

  const onFilesSelected = (files: FileList | null) => {
    if (!files) return;
    const list = Array.from(files);
    // Basic constraints: max 5 files, max 10MB each
    const filtered = list.filter((f) => f.size <= 10 * 1024 * 1024).slice(0, 5);
    const toAdd: SelectedFile[] = filtered.map((f) => ({
      file: f,
      status: 'queued',
      previewUrl: f.type.startsWith('image/')
        ? URL.createObjectURL(f)
        : undefined,
      note: '',
    }));
    setSelectedFiles((prev) => {
      const combined = [...prev, ...toAdd];
      // unique by name+size to avoid duplicates
      const seen = new Set<string>();
      const unique: SelectedFile[] = [];
      for (const sf of combined) {
        const key = `${sf.file.name}-${sf.file.size}`;
        if (!seen.has(key)) {
          seen.add(key);
          unique.push(sf);
        }
      }
      return unique.slice(0, 5);
    });
  };

  const removeSelectedFile = (idx: number) => {
    setSelectedFiles((prev) => {
      const copy = [...prev];
      const [removed] = copy.splice(idx, 1);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return copy;
    });
  };

  // Drag-and-drop handlers
  const onDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };
  const onDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const dt = e.dataTransfer;
    onFilesSelected(dt.files);
  };

  // Cache signed URLs for image attachments displayed in list
  useEffect(() => {
    let cancelled = false;
    async function preload() {
      const needed: { id: number; bucket: string; path: string }[] = [];
      for (const c of comments) {
        if (!c.attachments) continue;
        for (const a of c.attachments) {
          if (a.deleted_at) continue;
          if (!a.is_image) continue;
          if (!imageUrlCache[a.id]) {
            needed.push({ id: a.id, bucket: a.bucket, path: a.path });
          }
        }
      }
      if (needed.length === 0) return;
      const entries: [number, string][] = [];
      for (const item of needed) {
        try {
          const url = await getMarketCommentAttachmentSignedUrl(
            item.bucket,
            item.path,
            300
          );
          if (cancelled) return;
          entries.push([item.id, url]);
        } catch {}
      }
      if (entries.length > 0 && !cancelled) {
        setImageUrlCache((prev) => ({
          ...prev,
          ...Object.fromEntries(entries),
        }));
      }
    }
    preload();
    return () => {
      cancelled = true;
    };
  }, [comments]);

  // Small helper to display relative time
  function timeAgo(date: Date): string {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    const rtf = new Intl.RelativeTimeFormat('es', { numeric: 'auto' });
    const intervals: [number, Intl.RelativeTimeFormatUnit][] = [
      [60, 'second'],
      [60, 'minute'],
      [24, 'hour'],
      [7, 'day'],
      [4.34524, 'week'],
      [12, 'month'],
      [Number.POSITIVE_INFINITY, 'year'],
    ];
    let duration = seconds;
    let unit: Intl.RelativeTimeFormatUnit = 'second';
    for (let i = 0, acc = 1; i < intervals.length; i++) {
      const [step, u] = intervals[i];
      if (Math.abs(duration) < step) {
        unit = u;
        break;
      }
      duration = duration / step;
    }
    const value = Math.round(-duration);
    return rtf.format(value as any, unit);
  }

  // Order options so typed value appears first, then the rest
  const orderOptions = (input: string, arr: string[]) => {
    if (!input) return arr;
    const low = input.toLowerCase();
    const starts = arr.filter((v) => v.toLowerCase().startsWith(low));
    const contains = arr.filter(
      (v) => !v.toLowerCase().startsWith(low) && v.toLowerCase().includes(low)
    );
    const rest = arr.filter((v) => !v.toLowerCase().includes(low));
    return [...starts, ...contains, ...rest];
  };

  // Format YYYY-MM-DD to DD/MM/YY
  function formatDateShort(dateStr?: string | null): string {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    if (!y || !m || !d) return dateStr;
    return `${d}/${m}/${y.slice(-2)}`;
  }

  // Compute and preload default dates based on current selection
  useEffect(() => {
    let df = '';
    let dt = '';
    if (viewMode === 'day' && selectedDate) {
      df = selectedDate;
      dt = '';
    } else if (viewMode !== 'day' && dateRange) {
      const [from, to] = dateRange;
      df = from.toISOString().split('T')[0];
      dt = to.toISOString().split('T')[0];
    }
    if (!df) return;
    setForm((f) => ({ ...f, date_from: df, date_to: dt }));
  }, [viewMode, selectedDate, dateRange]);

  // Load distinct terms when opening the create modal or filters
  useEffect(() => {
    let cancelled = false;
    async function loadTerms() {
      if ((!isCreateModalOpen && !showFilters) || !exporterId) return;
      try {
        const data = await listMarketCommentTerms({
          exporter_id: exporterId,
          species_id: speciesId,
          season: season || undefined,
          limit: 1000,
        });
        if (!cancelled) setTerms(data);
      } catch {}
    }
    loadTerms();
    return () => {
      cancelled = true;
    };
  }, [isCreateModalOpen, showFilters, exporterId, speciesId, season]);

  // Load markets when opening the create modal
  useEffect(() => {
    let cancelled = false;
    async function loadMarkets() {
      if (!isCreateModalOpen) return;
      try {
        const data = await getMarkets();
        if (!cancelled) setMarkets(data);
      } catch {}
    }
    loadMarkets();
    return () => {
      cancelled = true;
    };
  }, [isCreateModalOpen]);

  // Open empty create modal (reset any prior edit state)
  const openCreateModal = () => {
    setIsEditMode(false);
    setEditingCommentId(null);
    setSelectedFiles([]);
    // Preserve current preloaded dates; reset the rest
    setForm((prev) => ({
      content: '',
      date_from: prev.date_from,
      date_to: prev.date_to,
      variety: '',
      caliber: '',
      color: '',
      ship_name: '',
      market_id: '',
      fleet: '',
      seller: '',
      exporter_ref: '',
      container_number: '',
      label: '',
      unit_price: '',
      unit_weight: '',
      unit_count: '',
      local_currency: prev.local_currency,
    }));
    setIsCreateModalOpen(true);
  };

  // En modo embebido la Bitácora ya va dentro de una Card: sus bloques
  // internos (filtros y comentarios) no pueden ser otra card (design.md › Cards).
  const renderBlock = (children: ReactNode, key?: Key, className = '') =>
    variant === 'embedded' ? (
      <div
        key={key}
        className={`rounded-medium border border-default-200 p-4 ${className}`}
      >
        {children}
      </div>
    ) : (
      <Card key={key} shadow='sm' className={className}>
        <CardBody>{children}</CardBody>
      </Card>
    );

  const SidebarContent = (
    <div className='flex flex-col h-full'>
      <div className='p-3 flex items-center justify-between border-b'>
        <div className='font-semibold'>{t('Bitácora')}</div>
        <div className='flex items-center gap-2'>
          <Button
            isIconOnly
            size='sm'
            variant='light'
            aria-label={t('Filtros')}
            onPress={() => setShowFilters((v) => !v)}
          >
            <Icon icon='tabler:filter' />
          </Button>
          <Button
            size='sm'
            color='primary'
            onPress={openCreateModal}
            startContent={<Icon icon='tabler:plus' width={16} />}
          >
            {t('Comentario')}
          </Button>
          <Button
            isIconOnly
            size='sm'
            variant='light'
            aria-label={t('Cerrar')}
            onPress={onToggle}
            className='md:hidden'
          >
            <Icon icon='tabler:x' />
          </Button>
          {/* <Button size='sm' variant='flat' onPress={onToggle}>
            {isOpen ? 'Cerrar' : 'Abrir'}
          </Button> */}
        </div>
      </div>
      <div className='flex-1 overflow-auto p-3'>
        {/* List only (form moved to modal) */}
        {loading && (
          <div className='flex items-center justify-center py-6'>
            <Spinner size='sm' />
          </div>
        )}
        {error && <div className='text-danger text-sm'>{error}</div>}
        {!loading && !error && comments.length === 0 && (
          <div className='text-sm text-default-500'>
            {t('No hay comentarios para la selección.')}
          </div>
        )}
        {showFilters &&
          renderBlock(
              <div className='grid grid-cols-2 gap-2'>
                <Input
                  className='col-span-2'
                  placeholder={t('Buscar texto, etiquetas...')}
                  value={filterForm.search}
                  onValueChange={(v) =>
                    setFilterForm((f) => ({ ...f, search: v }))
                  }
                />
                <Autocomplete
                  isClearable
                  label={t('Variedad')}
                  allowsCustomValue
                  inputValue={filterForm.variety}
                  onInputChange={(v) =>
                    setFilterForm((f) => ({ ...f, variety: v || '' }))
                  }
                  selectedKey={
                    terms.varieties.includes(filterForm.variety)
                      ? filterForm.variety
                      : undefined
                  }
                  onSelectionChange={(key) =>
                    setFilterForm((f) => ({
                      ...f,
                      variety: key ? String(key) : '',
                    }))
                  }
                  onClear={() => setFilterForm((f) => ({ ...f, variety: '' }))}
                  placeholder={t('Todas')}
                >
                  {orderOptions(filterForm.variety, terms.varieties).map(
                    (v) => (
                      <AutocompleteItem key={v} value={v}>
                        {v}
                      </AutocompleteItem>
                    )
                  )}
                </Autocomplete>
                <Autocomplete
                  isClearable
                  label={t('Calibre')}
                  allowsCustomValue
                  inputValue={filterForm.caliber}
                  onInputChange={(v) =>
                    setFilterForm((f) => ({ ...f, caliber: v || '' }))
                  }
                  selectedKey={
                    terms.calibers.includes(filterForm.caliber)
                      ? filterForm.caliber
                      : undefined
                  }
                  onSelectionChange={(key) =>
                    setFilterForm((f) => ({
                      ...f,
                      caliber: key ? String(key) : '',
                    }))
                  }
                  onClear={() => setFilterForm((f) => ({ ...f, caliber: '' }))}
                  placeholder={t('Todos')}
                >
                  {orderOptions(filterForm.caliber, terms.calibers).map((v) => (
                    <AutocompleteItem key={v} value={v}>
                      {v}
                    </AutocompleteItem>
                  ))}
                </Autocomplete>
                <Autocomplete
                  isClearable
                  label={t('Cliente')}
                  allowsCustomValue
                  inputValue={filterForm.seller}
                  onInputChange={(v) =>
                    setFilterForm((f) => ({ ...f, seller: v || '' }))
                  }
                  selectedKey={
                    terms.sellers.includes(filterForm.seller)
                      ? filterForm.seller
                      : undefined
                  }
                  onSelectionChange={(key) =>
                    setFilterForm((f) => ({
                      ...f,
                      seller: key ? String(key) : '',
                    }))
                  }
                  onClear={() => setFilterForm((f) => ({ ...f, seller: '' }))}
                  placeholder={t('Todos')}
                >
                  {orderOptions(filterForm.seller, terms.sellers).map((v) => (
                    <AutocompleteItem key={v} value={v}>
                      {v}
                    </AutocompleteItem>
                  ))}
                </Autocomplete>
                <Autocomplete
                  isClearable
                  label={t('Etiqueta')}
                  allowsCustomValue
                  inputValue={filterForm.label}
                  onInputChange={(v) =>
                    setFilterForm((f) => ({ ...f, label: v || '' }))
                  }
                  selectedKey={
                    terms.labels.includes(filterForm.label)
                      ? filterForm.label
                      : undefined
                  }
                  onSelectionChange={(key) =>
                    setFilterForm((f) => ({
                      ...f,
                      label: key ? String(key) : '',
                    }))
                  }
                  onClear={() => setFilterForm((f) => ({ ...f, label: '' }))}
                  placeholder={t('Todas')}
                >
                  {orderOptions(filterForm.label, terms.labels).map((v) => (
                    <AutocompleteItem key={v} value={v}>
                      {v}
                    </AutocompleteItem>
                  ))}
                </Autocomplete>
                <Autocomplete
                  isClearable
                  label={t('Referencia')}
                  allowsCustomValue
                  inputValue={filterForm.exporter_ref}
                  onInputChange={(v) =>
                    setFilterForm((f) => ({ ...f, exporter_ref: v || '' }))
                  }
                  selectedKey={
                    terms.exporter_refs.includes(filterForm.exporter_ref)
                      ? filterForm.exporter_ref
                      : undefined
                  }
                  onSelectionChange={(key) =>
                    setFilterForm((f) => ({
                      ...f,
                      exporter_ref: key ? String(key) : '',
                    }))
                  }
                  onClear={() =>
                    setFilterForm((f) => ({ ...f, exporter_ref: '' }))
                  }
                  placeholder={t('Todas')}
                >
                  {orderOptions(
                    filterForm.exporter_ref,
                    terms.exporter_refs
                  ).map((v) => (
                    <AutocompleteItem key={v} value={v}>
                      {v}
                    </AutocompleteItem>
                  ))}
                </Autocomplete>
                <Autocomplete
                  isClearable
                  label={t('Nro. Contenedor')}
                  allowsCustomValue
                  inputValue={filterForm.container_number}
                  onInputChange={(v) =>
                    setFilterForm((f) => ({ ...f, container_number: v || '' }))
                  }
                  selectedKey={
                    terms.container_numbers.includes(
                      filterForm.container_number
                    )
                      ? filterForm.container_number
                      : undefined
                  }
                  onSelectionChange={(key) =>
                    setFilterForm((f) => ({
                      ...f,
                      container_number: key ? String(key) : '',
                    }))
                  }
                  onClear={() =>
                    setFilterForm((f) => ({ ...f, container_number: '' }))
                  }
                  placeholder={t('Todos')}
                >
                  {orderOptions(
                    filterForm.container_number,
                    terms.container_numbers
                  ).map((v) => (
                    <AutocompleteItem key={v} value={v}>
                      {v}
                    </AutocompleteItem>
                  ))}
                </Autocomplete>
                <div className='col-span-2 flex justify-end gap-2'>
                  <Button
                    size='sm'
                    variant='light'
                    onPress={() =>
                      setFilterForm({
                        search: '',
                        variety: '',
                        caliber: '',
                        seller: '',
                        label: '',
                        exporter_ref: '',
                        container_number: '',
                      })
                    }
                  >
                    {t('Limpiar')}
                  </Button>
                </div>
              </div>,
            undefined,
            'mb-3'
          )}
        <div className='space-y-3'>
          {comments.map((c) =>
            renderBlock(
              <>
                <div className='flex w-full items-center justify-between text-xs text-default-500'>
                  <div className='font-medium truncate'>
                    {c.date_to && c.date_to !== c.date_from
                      ? `${formatDateShort(c.date_from)} → ${formatDateShort(c.date_to)}`
                      : formatDateShort(c.date_from)}
                  </div>
                  {/* Owner actions (icons fixed to the right) */}
                  {(typeof c.created_by === 'object'
                    ? (c.created_by as any).id
                    : c.created_by) === userId && (
                    <div className='flex items-center gap-2 shrink-0'>
                      <Tooltip content={t('Editar')}>
                        <Button
                          isIconOnly
                          size='sm'
                          variant='light'
                          aria-label={t('Editar')}
                          onPress={() => openEditModal(c)}
                        >
                          <Icon icon='tabler:pencil' />
                        </Button>
                      </Tooltip>
                      <Tooltip content={t('Duplicar')}>
                        <Button
                          isIconOnly
                          size='sm'
                          variant='light'
                          aria-label={t('Duplicar')}
                          onPress={() => openDuplicateModal(c)}
                        >
                          <Icon icon='tabler:copy' />
                        </Button>
                      </Tooltip>
                      <Tooltip content={t('Eliminar')}>
                        <Button
                          isIconOnly
                          size='sm'
                          color='danger'
                          variant='light'
                          aria-label={t('Eliminar')}
                          onPress={async () => {
                            try {
                              const ok = await confirm({
                                title: t('¿Eliminar este comentario?'),
                                message: t('Esta acción no puede deshacerse.'),
                                isDestructive: true,
                              });
                              if (!ok) return;
                              await deleteMarketComment(c.id);
                              setComments((prev) =>
                                prev.filter((mc) => mc.id !== c.id)
                              );
                              // Notify parent that bitácora data may have changed
                              if (onBitacoraChange) onBitacoraChange();
                            } catch {}
                          }}
                        >
                          <Icon icon='tabler:trash' />
                        </Button>
                      </Tooltip>
                    </div>
                  )}
                </div>
                {c.content ? (
                  <div className='mt-2 text-sm whitespace-pre-wrap'>
                    {c.content}
                  </div>
                ) : (
                  <div className='mt-2 text-sm italic text-default-500'>
                    {t('(Sin contenido)')}
                  </div>
                )}
                {/* Metadata block */}
                {(c.variety ||
                  c.caliber ||
                  c.color ||
                  c.ship_name ||
                  c.market_id ||
                  c.seller ||
                  c.label ||
                  c.exporter_ref ||
                  c.container_number ||
                  c.unit_weight ||
                  c.unit_price) && (
                  <div className='mt-2 rounded-md border border-default-200 bg-default-50 p-2'>
                    <div className='grid grid-cols-2 gap-x-4 gap-y-1 text-xs'>
                      {c.variety && (
                        <div>
                          <span className='text-default-500'>{t('Variedad:')} </span>
                          <span className='font-medium'>{c.variety}</span>
                        </div>
                      )}
                      {c.caliber && (
                        <div>
                          <span className='text-default-500'>{t('Calibre:')} </span>
                          <span className='font-medium'>{c.caliber}</span>
                        </div>
                      )}
                      {c.color && (
                        <div>
                          <span className='text-default-500'>{t('Color:')} </span>
                          <span className='font-medium'>{c.color}</span>
                        </div>
                      )}
                      {c.ship_name && (
                        <div>
                          <span className='text-default-500'>{t('Nave:')} </span>
                          <span className='font-medium'>{c.ship_name}</span>
                        </div>
                      )}
                      {c.market_id && (
                        <div>
                          <span className='text-default-500'>{t('Mercado:')} </span>
                          <span className='font-medium'>{c.market_id}</span>
                        </div>
                      )}
                      {c.seller && (
                        <div>
                          <span className='text-default-500'>{t('Cliente:')} </span>
                          <span className='font-medium'>{c.seller}</span>
                        </div>
                      )}
                      {c.label && (
                        <div>
                          <span className='text-default-500'>{t('Etiqueta:')} </span>
                          <span className='font-medium'>{c.label}</span>
                        </div>
                      )}
                      {c.format && (
                        <div>
                          <span className='text-default-500'>{t('Formato:')} </span>
                          <span className='font-medium'>{c.format}</span>
                        </div>
                      )}
                      {c.category && (
                        <div>
                          <span className='text-default-500'>{t('Categoría:')} </span>
                          <span className='font-medium'>{c.category}</span>
                        </div>
                      )}
                      {c.exporter_ref && (
                        <div>
                          <span className='text-default-500'>{t('Referencia:')} </span>
                          <span className='font-medium'>{c.exporter_ref}</span>
                        </div>
                      )}
                      {c.container_number && (
                        <div>
                          <span className='text-default-500'>{t('Contenedor:')} </span>
                          <span className='font-medium'>
                            {c.container_number}
                          </span>
                        </div>
                      )}
                      {/* Los bultos vendidos ya no se piden: el mercado vende
                          por palet. unit_count queda solo para ponderar. */}
                      {typeof c.unit_weight === 'number' && (
                        <div>
                          <span className='text-default-500'>{t('Peso bulto:')} </span>
                          <span className='font-medium'>
                            {c.unit_weight} kg
                          </span>
                        </div>
                      )}
                      {typeof c.unit_price === 'number' && (
                        <div>
                          <span className='text-default-500'>
                            {t('Precio bulto:')}{' '}
                          </span>
                          <span className='font-medium'>
                            {c.unit_price.toFixed(2)} {c.local_currency || ''}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {c.attachments &&
                  c.attachments.filter((a) => !a.deleted_at).length > 0 && (
                    <div className='mt-2 grid grid-cols-3 gap-2'>
                      {c.attachments
                        .filter((a) => !a.deleted_at)
                        .map((a) => (
                          <div
                            key={a.id}
                            className='relative group h-20 w-20 rounded border overflow-hidden'
                          >
                            {a.is_image ? (
                              <img
                                src={imageUrlCache[a.id]}
                                alt={a.name}
                                className='h-full w-full object-cover'
                              />
                            ) : (
                              <div className='h-full w-full flex items-center justify-center text-[10px] bg-default-100'>
                                {a.name?.split('.').pop()?.toUpperCase() ||
                                  'FILE'}
                              </div>
                            )}
                            <button
                              className='absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs flex items-center justify-center'
                              title={t('Ver')}
                              onClick={async () => {
                                try {
                                  if (a.is_image) {
                                    const url =
                                      imageUrlCache[a.id] ||
                                      (await getMarketCommentAttachmentSignedUrl(
                                        a.bucket,
                                        a.path,
                                        300
                                      ));
                                    setImageViewer({
                                      url,
                                      name: a.name,
                                      id: a.id,
                                      bucket: a.bucket,
                                      path: a.path,
                                      created_by: a.created_by as any,
                                      commentId: c.id,
                                      comment: a.comment || '',
                                      is_image: true,
                                    });
                                    setViewerNote(a.comment || '');
                                    setViewerEdit(false);
                                    setIsImageViewerOpen(true);
                                  } else {
                                    const url =
                                      await getMarketCommentAttachmentSignedUrl(
                                        a.bucket,
                                        a.path,
                                        120
                                      );
                                    window.open(url, '_blank');
                                  }
                                } catch {}
                              }}
                            >
                              {t('Ver')}
                            </button>
                          </div>
                        ))}
                    </div>
                  )}
                <Divider className='my-2' />
                <div className='text-xs text-default-400'>
                  {(() => {
                    const by =
                      typeof c.created_by === 'object'
                        ? `${(c.created_by as any).first_name || ''} ${(c.created_by as any).last_name || ''}`.trim()
                        : '';
                    return `${t('Creado por')} ${by || '—'}, ${timeAgo(new Date(c.created_at))}`;
                  })()}
                </div>
              </>,
              c.id
            )
          )}
        </div>
      </div>
      {/* Create comment modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onOpenChange={(open) => setIsCreateModalOpen(open)}
        portalContainer={modalPortalContainer}
        size='lg'
        scrollBehavior='inside'
        classNames={{
          wrapper: '!z-[10010]',
          base: '!z-[10010]',
          backdrop: '!z-[10000]',
          body: 'max-h-[70vh] overflow-y-auto',
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalBody>
                <div className='p-2'>
                  <div className='text-base font-semibold mb-2'>
                    {isEditMode ? 'Editar comentario' : 'Agregar comentario'}
                  </div>
                  <div className='flex flex-col gap-2 mb-2'>
                    <Input
                      type='date'
                      label='Desde'
                      value={form.date_from}
                      onValueChange={(v) =>
                        setForm((f) => ({ ...f, date_from: v }))
                      }
                    />
                    <Input
                      type='date'
                      label='Hasta'
                      description='Si quieres ingresar comentario para un rango de fechas, ingresa la fecha de fin (opcional)'
                      value={form.date_to}
                      onValueChange={(v) =>
                        setForm((f) => ({ ...f, date_to: v }))
                      }
                    />
                  </div>
                  <Textarea
                    label='Comentario'
                    placeholder='Escribe una nota...'
                    minRows={2}
                    value={form.content}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, content: v }))
                    }
                    className='mb-2'
                  />
                  <div className='mb-2'>
                    <div className='text-xs text-default-500 mb-1'>
                      Adjuntar archivos (imágenes o PDFs, máx 5)
                    </div>
                    <div
                      className={`border-2 border-dashed rounded-md p-3 text-center cursor-pointer ${
                        isDragging
                          ? 'border-primary bg-primary-50'
                          : 'border-default-200'
                      }`}
                      onDragOver={onDragOver}
                      onDragLeave={onDragLeave}
                      onDrop={onDrop}
                      onClick={() => {
                        const input = document.getElementById(
                          'file-input-market-comments'
                        ) as HTMLInputElement | null;
                        input?.click();
                      }}
                    >
                      <div className='text-sm'>
                        Arrastra y suelta archivos aquí o haz clic para
                        seleccionar
                      </div>
                      <input
                        id='file-input-market-comments'
                        type='file'
                        className='hidden'
                        multiple
                        accept='image/*,application/pdf'
                        onChange={(e) => onFilesSelected(e.target.files)}
                      />
                    </div>
                    {selectedFiles.length > 0 && (
                      <div className='mt-2 flex flex-col gap-2'>
                        {selectedFiles.map((s, idx) => (
                          <div
                            key={`${s.file.name}-${idx}`}
                            className='flex items-center gap-2 border rounded px-2 py-2'
                          >
                            {s.previewUrl ? (
                              <img
                                src={s.previewUrl}
                                alt={s.file.name}
                                className='h-12 w-12 object-cover rounded'
                              />
                            ) : (
                              <div className='h-12 w-12 flex items-center justify-center bg-default-100 rounded text-xs'>
                                FILE
                              </div>
                            )}
                            <div className='flex-1 min-w-0'>
                              <div
                                className='text-xs truncate'
                                title={s.file.name}
                              >
                                {s.file.name}
                              </div>
                              <div className='mt-1'>
                                <Input
                                  size='sm'
                                  placeholder='Nota (opcional)'
                                  value={s.note || ''}
                                  onValueChange={(v) =>
                                    setSelectedFiles((prev) => {
                                      const copy = [...prev];
                                      copy[idx] = { ...copy[idx], note: v };
                                      return copy;
                                    })
                                  }
                                />
                              </div>
                            </div>
                            <div className='text-[11px] w-24 text-right'>
                              {s.status === 'queued' && (
                                <span className='text-default-400'>
                                  Listo para subir
                                </span>
                              )}
                              {s.status === 'uploading' && (
                                <span className='text-primary'>
                                  Subiendo...
                                </span>
                              )}
                              {s.status === 'done' && (
                                <span className='text-success'>Listo</span>
                              )}
                              {s.status === 'error' && (
                                <span className='text-danger'>Error</span>
                              )}
                            </div>
                            <button
                              className='text-danger text-xs ml-2'
                              onClick={() => removeSelectedFile(idx)}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className='grid grid-cols-2 gap-2'>
                    <Autocomplete
                      isClearable
                      label='Variedad (opcional)'
                      allowsCustomValue
                      inputValue={form.variety}
                      onInputChange={(v) =>
                        setForm((f) => ({
                          ...f,
                          variety: (v || '').toUpperCase(),
                        }))
                      }
                      selectedKey={
                        terms.varieties.includes(form.variety)
                          ? form.variety
                          : undefined
                      }
                      onSelectionChange={(key) =>
                        setForm((f) => ({
                          ...f,
                          variety: key ? String(key) : '',
                        }))
                      }
                      onClear={() => setForm((f) => ({ ...f, variety: '' }))}
                      placeholder='Seleccione o escriba'
                    >
                      {orderOptions(form.variety, terms.varieties).map((v) => (
                        <AutocompleteItem key={v} value={v}>
                          {v}
                        </AutocompleteItem>
                      ))}
                    </Autocomplete>
                    <Autocomplete
                      isClearable
                      label='Calibre (opcional)'
                      allowsCustomValue
                      inputValue={form.caliber}
                      onInputChange={(v) =>
                        setForm((f) => ({
                          ...f,
                          caliber: (v || '').toUpperCase(),
                        }))
                      }
                      selectedKey={
                        terms.calibers.includes(form.caliber)
                          ? form.caliber
                          : undefined
                      }
                      onSelectionChange={(key) =>
                        setForm((f) => ({
                          ...f,
                          caliber: key ? String(key) : '',
                        }))
                      }
                      onClear={() => setForm((f) => ({ ...f, caliber: '' }))}
                      placeholder='Seleccione o escriba'
                    >
                      {orderOptions(form.caliber, terms.calibers).map((v) => (
                        <AutocompleteItem key={v} value={v}>
                          {v}
                        </AutocompleteItem>
                      ))}
                    </Autocomplete>
                    <Autocomplete
                      isClearable
                      label={t('Color') + ' (opcional)'}
                      allowsCustomValue
                      inputValue={form.color}
                      onInputChange={(v) =>
                        setForm((f) => ({
                          ...f,
                          color: (v || '').toUpperCase(),
                        }))
                      }
                      selectedKey={
                        speciesColors.includes(form.color)
                          ? form.color
                          : undefined
                      }
                      onSelectionChange={(key) =>
                        setForm((f) => ({
                          ...f,
                          color: key ? String(key) : '',
                        }))
                      }
                      onClear={() => setForm((f) => ({ ...f, color: '' }))}
                      placeholder={t('Selecciona o escribe')}
                    >
                      {speciesColors.map((c) => (
                        <AutocompleteItem key={c} value={c}>
                          {c}
                        </AutocompleteItem>
                      ))}
                    </Autocomplete>
                    <Autocomplete
                      isClearable
                      label={t('Mercado') + ' (opcional)'}
                      selectedKey={form.market_id || undefined}
                      onSelectionChange={(key) =>
                        setForm((f) => ({
                          ...f,
                          market_id: key ? String(key) : '',
                        }))
                      }
                      onClear={() => setForm((f) => ({ ...f, market_id: '' }))}
                      placeholder={t('Selecciona mercado')}
                    >
                      {markets.map((m) => (
                        <AutocompleteItem key={m.id} value={m.id}>
                          {m.name}
                        </AutocompleteItem>
                      ))}
                    </Autocomplete>
                    <Input
                      type='text'
                      label={t('Nave') + ' (opcional)'}
                      value={form.ship_name}
                      onValueChange={(v) =>
                        setForm((f) => ({ ...f, ship_name: v.toUpperCase() }))
                      }
                      placeholder={t('Nombre de la nave')}
                    />
                    <Select
                      label={t('Flete') + ' (opcional)'}
                      selectedKeys={form.fleet ? [form.fleet] : []}
                      onSelectionChange={(keys) => {
                        const arr = Array.from(keys);
                        setForm((f) => ({
                          ...f,
                          fleet: (arr[0] as 'A' | 'M' | '') || '',
                        }));
                      }}
                      placeholder={t('Selecciona flete')}
                    >
                      <SelectItem key='M' value='M'>
                        {t('MARITIMO')}
                      </SelectItem>
                      <SelectItem key='A' value='A'>
                        {t('AEREO')}
                      </SelectItem>
                    </Select>
                    <Autocomplete
                      isClearable
                      label={t('Cliente') + ' (opcional)'}
                      allowsCustomValue
                      inputValue={form.seller}
                      onInputChange={(v) =>
                        setForm((f) => ({
                          ...f,
                          seller: (v || '').toUpperCase(),
                        }))
                      }
                      selectedKey={
                        terms.sellers.includes(form.seller)
                          ? form.seller
                          : undefined
                      }
                      onSelectionChange={(key) =>
                        setForm((f) => ({
                          ...f,
                          seller: key ? String(key) : '',
                        }))
                      }
                      onClear={() => setForm((f) => ({ ...f, seller: '' }))}
                      placeholder='Seleccione o escriba'
                    >
                      {orderOptions(form.seller, terms.sellers).map((v) => (
                        <AutocompleteItem key={v} value={v}>
                          {v}
                        </AutocompleteItem>
                      ))}
                    </Autocomplete>
                    <Autocomplete
                      isClearable
                      label='Etiqueta (opcional)'
                      allowsCustomValue
                      inputValue={form.label}
                      onInputChange={(v) =>
                        setForm((f) => ({
                          ...f,
                          label: (v || '').toUpperCase(),
                        }))
                      }
                      selectedKey={
                        terms.labels.includes(form.label)
                          ? form.label
                          : undefined
                      }
                      onSelectionChange={(key) =>
                        setForm((f) => ({
                          ...f,
                          label: key ? String(key) : '',
                        }))
                      }
                      onClear={() => setForm((f) => ({ ...f, label: '' }))}
                      placeholder='Seleccione o escriba'
                    >
                      {orderOptions(form.label, terms.labels).map((v) => (
                        <AutocompleteItem key={v} value={v}>
                          {v}
                        </AutocompleteItem>
                      ))}
                    </Autocomplete>
                    <Autocomplete
                      isClearable
                      label='Referencia (opcional)'
                      allowsCustomValue
                      inputValue={form.exporter_ref}
                      onInputChange={(v) =>
                        setForm((f) => ({
                          ...f,
                          exporter_ref: (v || '').toUpperCase(),
                        }))
                      }
                      selectedKey={
                        terms.exporter_refs.includes(form.exporter_ref)
                          ? form.exporter_ref
                          : undefined
                      }
                      onSelectionChange={(key) =>
                        setForm((f) => ({
                          ...f,
                          exporter_ref: key ? String(key) : '',
                        }))
                      }
                      onClear={() =>
                        setForm((f) => ({ ...f, exporter_ref: '' }))
                      }
                      placeholder='Seleccione o escriba'
                    >
                      {orderOptions(form.exporter_ref, terms.exporter_refs).map(
                        (v) => (
                          <AutocompleteItem key={v} value={v}>
                            {v}
                          </AutocompleteItem>
                        )
                      )}
                    </Autocomplete>
                    <Autocomplete
                      isClearable
                      label='Nro. Contenedor (opcional)'
                      allowsCustomValue
                      inputValue={form.container_number}
                      onInputChange={(v) =>
                        setForm((f) => ({
                          ...f,
                          container_number: (v || '').toUpperCase(),
                        }))
                      }
                      selectedKey={
                        terms.container_numbers.includes(form.container_number)
                          ? form.container_number
                          : undefined
                      }
                      onSelectionChange={(key) =>
                        setForm((f) => ({
                          ...f,
                          container_number: key ? String(key) : '',
                        }))
                      }
                      onClear={() =>
                        setForm((f) => ({ ...f, container_number: '' }))
                      }
                      placeholder='Seleccione o escriba'
                    >
                      {orderOptions(
                        form.container_number,
                        terms.container_numbers
                      ).map((v) => (
                        <AutocompleteItem key={v} value={v}>
                          {v}
                        </AutocompleteItem>
                      ))}
                    </Autocomplete>
                    <Input
                      type='number'
                      label='Cantidad de Bultos (opcional)'
                      value={form.unit_count}
                      onValueChange={(v) =>
                        setForm((f) => ({ ...f, unit_count: v }))
                      }
                    />
                    <Input
                      type='number'
                      label='Peso Bulto (kg) (opcional)'
                      value={form.unit_weight}
                      onValueChange={(v) =>
                        setForm((f) => ({ ...f, unit_weight: v }))
                      }
                    />
                    <Input
                      type='number'
                      label='Precio Bulto/Caja (opcional)'
                      value={form.unit_price}
                      onValueChange={(v) =>
                        setForm((f) => ({ ...f, unit_price: v }))
                      }
                      isDisabled={!(Number(form.unit_weight) > 0)}
                      description={
                        !(Number(form.unit_weight) > 0)
                          ? 'Ingresa Peso Bulto (kg) para habilitar el precio'
                          : undefined
                      }
                    />

                    <Input
                      type='text'
                      label='Moneda (opcional)'
                      value={form.local_currency}
                      onValueChange={(v) =>
                        setForm((f) => ({
                          ...f,
                          local_currency: v.toUpperCase(),
                        }))
                      }
                    />
                  </div>
                  {error && (
                    <p className='mt-3 text-sm text-danger'>{error}</p>
                  )}
                  <div className='mt-3 flex justify-end gap-2'>
                    <Button
                      size='sm'
                      variant='light'
                      onPress={() => {
                        setIsCreateModalOpen(false);
                        setIsEditMode(false);
                        setEditingCommentId(null);
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      size='sm'
                      color='primary'
                      isDisabled={
                        creating ||
                        uploadingAttachments ||
                        !exporterId ||
                        !userId ||
                        (!!form.unit_price && !(Number(form.unit_weight) > 0))
                      }
                      isLoading={creating || uploadingAttachments}
                      onPress={async () => {
                        // Antes se cerraba aunque fallara: el error quedaba
                        // en la lista, detrás del modal, y el registro se perdía.
                        if (await handleCreate()) setIsCreateModalOpen(false);
                      }}
                    >
                      {isEditMode ? 'Guardar cambios' : 'Guardar comentario'}
                    </Button>
                  </div>
                </div>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
      {/* Image viewer modal */}
      <Modal
        isOpen={isImageViewerOpen}
        onOpenChange={setIsImageViewerOpen}
        size='lg'
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalBody>
                {imageViewer && (
                  <div className='p-2'>
                    <div className='flex items-start justify-between mb-2 gap-3'>
                      <div className='flex-1'>
                        {!viewerEdit ? (
                          <div className='text-sm whitespace-pre-wrap'>
                            {imageViewer.comment &&
                            imageViewer.comment.trim().length > 0 ? (
                              imageViewer.comment
                            ) : (
                              <span className='italic text-default-400'>
                                (Sin nota)
                              </span>
                            )}
                          </div>
                        ) : (
                          <Input
                            size='sm'
                            placeholder='Nota del adjunto'
                            value={viewerNote}
                            onValueChange={setViewerNote}
                          />
                        )}
                      </div>
                      {userId && imageViewer.created_by === userId && (
                        <div className='flex items-center gap-2 shrink-0'>
                          {!viewerEdit ? (
                            <Button
                              isIconOnly
                              size='sm'
                              variant='light'
                              aria-label='Editar nota'
                              onPress={() => {
                                setViewerEdit(true);
                                setViewerNote(imageViewer.comment || '');
                              }}
                            >
                              <Icon icon='tabler:pencil' />
                            </Button>
                          ) : (
                            <>
                              <Button
                                size='sm'
                                color='primary'
                                onPress={async () => {
                                  try {
                                    const updated =
                                      await updateMarketCommentAttachment(
                                        imageViewer.id,
                                        { comment: viewerNote || null }
                                      );
                                    setComments((prev) =>
                                      prev.map((mc) =>
                                        mc.id === imageViewer.commentId
                                          ? {
                                              ...mc,
                                              attachments: (
                                                mc.attachments || []
                                              ).map((att) =>
                                                att.id === imageViewer.id
                                                  ? {
                                                      ...att,
                                                      comment:
                                                        updated.comment || null,
                                                    }
                                                  : att
                                              ),
                                            }
                                          : mc
                                      )
                                    );
                                    setImageViewer({
                                      ...imageViewer,
                                      comment: updated.comment || '',
                                    });
                                    setViewerEdit(false);
                                  } catch {}
                                }}
                              >
                                Guardar
                              </Button>
                              <Button
                                size='sm'
                                variant='light'
                                onPress={() => setViewerEdit(false)}
                              >
                                Cancelar
                              </Button>
                            </>
                          )}
                          <Button
                            isIconOnly
                            size='sm'
                            color='danger'
                            variant='light'
                            aria-label='Eliminar'
                            onPress={async () => {
                              try {
                                const ok = await confirm({
                                  title: t('¿Eliminar este adjunto?'),
                                  message: t('Esta acción no puede deshacerse.'),
                                  isDestructive: true,
                                });
                                if (!ok) return;
                                await deleteMarketCommentAttachment({
                                  id: imageViewer.id,
                                  bucket: imageViewer.bucket,
                                  path: imageViewer.path,
                                });
                                setComments((prev) =>
                                  prev.map((mc) =>
                                    mc.id === imageViewer.commentId
                                      ? {
                                          ...mc,
                                          attachments: (
                                            mc.attachments || []
                                          ).map((att) =>
                                            att.id === imageViewer.id
                                              ? {
                                                  ...att,
                                                  deleted_at:
                                                    new Date().toISOString() as any,
                                                }
                                              : att
                                          ),
                                        }
                                      : mc
                                  )
                                );
                                setIsImageViewerOpen(false);
                              } catch {}
                            }}
                          >
                            <Icon icon='tabler:trash' />
                          </Button>
                        </div>
                      )}
                    </div>
                    {imageViewer.is_image ? (
                      <img
                        src={imageViewer.url}
                        alt={imageViewer.name}
                        className='max-h-[70vh] w-full object-contain rounded border'
                      />
                    ) : (
                      <div className='p-4 text-sm'>
                        Este adjunto no es una imagen.{' '}
                        <button
                          className='text-primary underline'
                          onClick={async () => {
                            const url =
                              await getMarketCommentAttachmentSignedUrl(
                                imageViewer.bucket,
                                imageViewer.path,
                                120
                              );
                            window.open(url, '_blank');
                          }}
                        >
                          Abrir
                        </button>
                      </div>
                    )}
                    <div
                      className='text-xs text-default-500 mt-2 truncate'
                      title={imageViewer.name}
                    >
                      {imageViewer.name}
                    </div>
                  </div>
                )}
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );

  if (variant === 'embedded') {
    return (
      <Card shadow='sm' className='h-full w-full'>
        {SidebarContent}
      </Card>
    );
  }

  if (isMd) {
    const desktopStyle: CSSProperties = {
      width: isOpen ? 384 : 0,
      minWidth: isOpen ? 384 : 0,
      flexBasis: isOpen ? 384 : 0,
      opacity: isOpen ? 1 : 0,
      pointerEvents: isOpen ? 'auto' : 'none',
    };

    // Desktop: behave like left filters sidebar, affecting layout width
    return (
      <div
        className='h-screen bg-background shadow-md transition-all duration-300 overflow-hidden border-l relative z-30 shrink-0'
        style={desktopStyle}
      >
        <div className='h-full w-96'>{SidebarContent}</div>
      </div>
    );
  }

  // Mobile: full-screen modal
  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onToggle}
      hideCloseButton
      classNames={{
        base: 'justify-end m-0 p-0 h-dvh max-h-full w-screen z-[50]',
        wrapper: 'items-end justify-end !w-screen',
        body: 'p-0 h-dvh',
        closeButton: 'z-50',
      }}
      radius='none'
      scrollBehavior='inside'
      isDismissable={false}
    >
      <ModalContent>
        <ModalBody>{SidebarContent}</ModalBody>
      </ModalContent>
    </Modal>
  );
}
